import { useCallback } from 'react';

import {
  ControlAppSchemasUserClientResponse,
  ControlClientUpdateRequest,
  V1Api,
} from '@explorer/open-api-client';
import {
  useExplorerAPIMutationParamType,
  useV1APIMutation,
} from '@explorer/query';
import { useQueryClient } from '@tanstack/react-query';

type MutateArgs = { clientId: string } & ControlClientUpdateRequest;

export const useMutationUpdateClient = (
  args?: Omit<
    useExplorerAPIMutationParamType<V1Api, ControlAppSchemasUserClientResponse>,
    'mutationFn'
  >,
) => {
  const client = useQueryClient();

  const { onSuccess: onSuccessCallback, ...otherArgs } = args ?? {};

  const onSuccess: typeof onSuccessCallback = useCallback(
    (data, mutateArgs: MutateArgs, context) => {
      const { clientId } = mutateArgs;

      client.invalidateQueries([
        'v1ControlWebAdminGetClientInfoClientIdGet',
        clientId,
      ]);

      onSuccessCallback?.(data, mutateArgs, context);
    },
    [client, onSuccessCallback],
  );

  return useV1APIMutation<ControlAppSchemasUserClientResponse, MutateArgs>({
    mutationFn(
      mArgs: { clientId: string } & ControlClientUpdateRequest,
      mClient,
    ) {
      const { clientId, ...rest } = mArgs;

      return mClient.v1ControlWebAdminClientUpdateClientIdPost({
        client_id: clientId,
        ControlClientUpdateRequest: rest,
      });
    },
    onSuccess: (...successArgs) => {
      onSuccess(...successArgs);
    },
    ...otherArgs,
  });
};
